import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Container, Form, Table } from 'react-bootstrap';
import { onValue, ref } from 'firebase/database';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface LearningHistoryEntry {
  id: string;
  setId: string;
  setName?: string | null;
  startedAt?: number;
  finishedAt?: number;
  totalCards?: number;
  answeredCount?: number;
  knownCount?: number;
  unknownCount?: number;
  shuffled?: boolean;
  startOnBack?: boolean;
  limit?: number | null;
}

const LearningCardsHistory = () => {
  const { currentUser } = useAuth();

  const [entries, setEntries] = useState<LearningHistoryEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterDate, setFilterDate] = useState('');
  const [filterSet, setFilterSet] = useState('');
  const [filterKnownMin, setFilterKnownMin] = useState('');
  const [filterUnknownMin, setFilterUnknownMin] = useState('');
  const [filterTotalMin, setFilterTotalMin] = useState('');

  useEffect(() => {
    if (!currentUser) return;

    const historyRef = ref(database, `learning_cards/${currentUser.uid}/history`);
    const unsub = onValue(
      historyRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const list = Object.entries(data).map(([id, value]: [string, any]) => ({
          id,
          setId: value?.setId || '',
          setName: value?.setName ?? null,
          startedAt: value?.startedAt,
          finishedAt: value?.finishedAt,
          totalCards: value?.totalCards,
          answeredCount: value?.answeredCount,
          knownCount: value?.knownCount,
          unknownCount: value?.unknownCount,
          shuffled: value?.shuffled,
          startOnBack: value?.startOnBack,
          limit: value?.limit ?? null
        }));

        list.sort((a, b) => (b.finishedAt ?? b.startedAt ?? 0) - (a.finishedAt ?? a.startedAt ?? 0));
        setEntries(list);
        setLoadError(null);
      },
      () => {
        setLoadError('Fehler beim Laden der Lernhistorie.');
      }
    );

    return () => unsub();
  }, [currentUser]);

  const formatDateTime = useMemo(() => {
    return (ts?: number) => {
      if (!ts) return '-';
      try {
        return new Date(ts).toLocaleString('de-DE');
      } catch {
        return '-';
      }
    };
  }, []);

  const filteredEntries = useMemo(() => {
    const dateNeedle = filterDate.trim();
    const setNeedle = filterSet.trim().toLowerCase();

    const parseMin = (raw: string) => {
      const t = raw.trim();
      if (!t) return null;
      const n = Number(t);
      if (!Number.isFinite(n)) return null;
      return Math.floor(n);
    };

    const knownMin = parseMin(filterKnownMin);
    const unknownMin = parseMin(filterUnknownMin);
    const totalMin = parseMin(filterTotalMin);

    return entries.filter((e) => {
      const ts = e.finishedAt ?? e.startedAt;
      const dateKey = ts ? new Date(ts).toISOString().slice(0, 10) : '';

      if (dateNeedle && !dateKey.includes(dateNeedle)) return false;

      const setKey = `${e.setName || ''} ${e.setId || ''}`.toLowerCase();
      if (setNeedle && !setKey.includes(setNeedle)) return false;

      const known = e.knownCount ?? 0;
      const unknown = e.unknownCount ?? 0;
      const total = e.totalCards ?? 0;

      if (knownMin !== null && known < knownMin) return false;
      if (unknownMin !== null && unknown < unknownMin) return false;
      if (totalMin !== null && total < totalMin) return false;

      return true;
    });
  }, [entries, filterDate, filterKnownMin, filterSet, filterTotalMin, filterUnknownMin]);

  const chartEntries = useMemo(() => {
    const copy = [...filteredEntries];
    copy.sort((a, b) => (a.finishedAt ?? a.startedAt ?? 0) - (b.finishedAt ?? b.startedAt ?? 0));
    return copy;
  }, [filteredEntries]);

  const avgKnownPct = useMemo(() => {
    if (chartEntries.length === 0) return null;
    const pcts = chartEntries
      .map((e) => {
        const total = e.totalCards ?? 0;
        if (total <= 0) return null;
        const known = e.knownCount ?? 0;
        return (known / total) * 100;
      })
      .filter((v): v is number => v !== null);
    if (pcts.length === 0) return null;
    const avg = pcts.reduce((a, b) => a + b, 0) / pcts.length;
    return Math.round(avg);
  }, [chartEntries]);

  const chartData = useMemo(() => {
    const labels = chartEntries.map((e) => {
      const ts = e.finishedAt ?? e.startedAt;
      if (!ts) return '-';
      try {
        return new Date(ts).toLocaleString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return '-';
      }
    });

    const knownPct = chartEntries.map((e) => {
      const total = e.totalCards ?? 0;
      if (total <= 0) return 0;
      const known = e.knownCount ?? 0;
      return Math.round((known / total) * 100);
    });

    const avgLine = avgKnownPct === null ? null : chartEntries.map(() => avgKnownPct);

    return {
      labels,
      datasets: [
        {
          label: 'Gewusst (%)',
          data: knownPct,
          borderColor: '#198754',
          backgroundColor: 'rgba(25, 135, 84, 0.15)',
          pointBackgroundColor: '#198754',
          tension: 0.25
        },
        ...(avgLine
          ? [
              {
                label: `Ø Gewusst (${avgKnownPct}%)`,
                data: avgLine,
                borderColor: 'rgba(108, 117, 125, 0.9)',
                backgroundColor: 'rgba(0, 0, 0, 0)',
                pointRadius: 0,
                borderDash: [6, 6]
              }
            ]
          : [])
      ]
    };
  }, [avgKnownPct, chartEntries]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      plugins: {
        legend: { display: true, position: 'top' as const },
        title: { display: true, text: 'Lernerfolg (Gewusst in %)' }
      },
      scales: {
        y: {
          min: 0,
          max: 100,
          ticks: { callback: (value: any) => `${value}%` }
        }
      }
    };
  }, []);

  if (!currentUser) {
    return (
      <Container fluid>
        <h2 className="mb-4">Learning History</h2>
        <Card>
          <Card.Body>
            <p>Bitte melden Sie sich an, um Learning History zu verwenden.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">Learning History</h2>
        <Link to="/dashboard/learning-cards" className="btn btn-outline-primary">
          Zurück
        </Link>
      </div>

      {loadError && <Alert variant="danger">{loadError}</Alert>}

      {entries.length === 0 ? (
        <Alert variant="info">Noch keine Lern-Sessions gespeichert.</Alert>
      ) : (
        <Card>
          <Card.Body>
            <div className="mb-4">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <div className="fw-bold">Lernerfolg</div>
                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                  {avgKnownPct === null ? '—' : `Ø Gewusst: ${avgKnownPct}%`} (gefiltert)
                </div>
              </div>
              {chartEntries.length === 0 ? (
                <Alert variant="light" className="mb-0">
                  Keine Einträge für die aktuellen Filter.
                </Alert>
              ) : (
                <div style={{ height: '260px' }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end mb-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setFilterDate('');
                  setFilterSet('');
                  setFilterKnownMin('');
                  setFilterUnknownMin('');
                  setFilterTotalMin('');
                }}
              >
                Filter zurücksetzen
              </button>
            </div>
            <Table responsive className="mb-0">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Set</th>
                  <th className="text-end">Gewusst</th>
                  <th className="text-end">Nicht gewusst</th>
                  <th className="text-end">Total</th>
                </tr>
                <tr>
                  <th style={{ minWidth: '140px' }}>
                    <Form.Control
                      size="sm"
                      placeholder="YYYY-MM-DD"
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                    />
                  </th>
                  <th style={{ minWidth: '160px' }}>
                    <Form.Control
                      size="sm"
                      placeholder="Set filtern..."
                      value={filterSet}
                      onChange={(e) => setFilterSet(e.target.value)}
                    />
                  </th>
                  <th style={{ minWidth: '120px' }}>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      step={1}
                      placeholder="min"
                      className="text-end"
                      value={filterKnownMin}
                      onChange={(e) => setFilterKnownMin(e.target.value)}
                    />
                  </th>
                  <th style={{ minWidth: '140px' }}>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      step={1}
                      placeholder="min"
                      className="text-end"
                      value={filterUnknownMin}
                      onChange={(e) => setFilterUnknownMin(e.target.value)}
                    />
                  </th>
                  <th style={{ minWidth: '120px' }}>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      step={1}
                      placeholder="min"
                      className="text-end"
                      value={filterTotalMin}
                      onChange={(e) => setFilterTotalMin(e.target.value)}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((e) => (
                  <tr key={e.id}>
                    <td>{formatDateTime(e.finishedAt ?? e.startedAt)}</td>
                    <td>{e.setName || e.setId}</td>
                    <td className="text-end">{e.knownCount ?? '-'}</td>
                    <td className="text-end">{e.unknownCount ?? '-'}</td>
                    <td className="text-end">{e.totalCards ?? '-'}</td>
                  </tr>
                ))}
                {filteredEntries.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      Keine Einträge für die aktuellen Filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default LearningCardsHistory;
